// ------------------------------------------------------------------
// BigLoads Owner Presell — SHARED CONTENT (single source of truth).
//
// All three layout variants (Rambo / Grok / Fable) import this module so
// the COPY is byte-identical across variants. That isolates DESIGN as the
// only test variable = a clean A/B on layout. Never fork copy per-variant;
// edit it here once.
//
// Source of truth: bigloads/presell-mockups/BENEFITS-LOCKED.md
// Copy rules: no em-dashes in prose; never promise done-for-you SEO/social.
// ------------------------------------------------------------------

const SITE = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://www.bigloads.com';
const CANONICAL = `${SITE}/list-your-trailer`;

const TITLE =
  'List Your Trailer for Rent | Make Money Renting Out Your Trailer | BigLoads';
const DESC =
  'List your trailer for rent free on BigLoads and get found by renters searching in your area. No commissions, no booking fees. Upgrade to Haul Star for direct renter contact. $30/mo covers your whole account.';

// Newsletter signup -> The Weekly Load (Mailchimp us3, list 67778e636d).
const NEWSLETTER_URL =
  process.env.REACT_APP_WEEKLY_LOAD_SIGNUP_URL ||
  'https://mefirstliving.us3.list-manage.com/subscribe?u=&id=67778e636d';

const CHANNELS = [
  { ic: '📘', h: 'Facebook', p: 'Reach local buyers in your community' },
  { ic: '🔍', h: 'Google', p: 'Get found in search results' },
  { ic: '🗣️', h: 'Word of Mouth', p: 'Happy renters spread the word' },
  { ic: '🌐', h: 'Your Website', p: 'Keep doing what already works' },
];

const CONTROL = [
  { ic: '🚚', h: 'Your Trailers', p: 'List the equipment you already own, when you want.' },
  { ic: '💲', h: 'Your Rates', p: 'You set the pricing, deposits, and rental terms.' },
  {
    ic: '🤝',
    h: 'Your Customers',
    p: 'Renters contact your business and deal directly with you.',
  },
  { ic: '⚙️', h: 'Your Process', p: 'Keep your own contracts, payments, and procedures.' },
];

const STEPS = [
  {
    n: '1',
    h: 'List your trailers',
    p: 'Create your business profile and add your inventory in minutes. Photos, details, and your location.',
  },
  {
    n: '2',
    h: 'Renters search locally',
    p: 'People looking for car haulers, dump trailers, utility trailers, and more find your listings in their area.',
  },
  {
    n: '3',
    h: 'You handle the rental',
    p: 'You decide availability, pricing, deposits, and terms. You manage the rental exactly like you always do.',
  },
];

const TABLE_ROWS = [
  { f: 'Appear in BigLoads search', free: true, hs: true },
  { f: 'Receive BigLoads messages', free: true, hs: true },
  { f: 'SEO-optimized listings', free: true, hs: true },
  { f: 'Owner Toolbox access', free: true, hs: true },
  { f: 'Haul Star Verified badge', free: false, hs: true },
  { f: 'Priority search placement', free: false, hs: true },
  { f: 'Business name & logo displayed', free: false, hs: true },
  { f: 'Phone number displayed', free: false, hs: true },
  { f: 'Email displayed', free: false, hs: true },
  { f: 'Website link (dofollow) displayed', free: false, hs: true },
  { f: 'Social media links displayed', free: false, hs: true },
  { f: 'Import Google Reviews', free: false, hs: true },
  { f: 'Contact info on listing photos', free: false, hs: true },
  { f: 'Direct renter contact', free: false, hs: true },
  { f: 'Discount badges on your listings', free: false, hs: true },
  { f: 'Insurance trust badges', free: false, hs: true },
];

const HS_OFFER = [
  'Haul Star Verified badge',
  'Business name, logo & branding on every listing',
  'Phone, email & website displayed',
  'Social media links + Google Reviews imported',
  'Contact info right on your listing photos',
  'Priority search placement + SEO-optimized listings',
  'Direct renter contact, no account needed',
  'Discount badges renters can see and ask about',
  'Insurance trust badges on your listings',
  'Covers your entire account, not one trailer',
  'No commission on your rentals, ever',
];

const PAINS = [
  { ic: '🔧', s: 'Damage happens' },
  { ic: '🛞', s: 'Tire blowouts' },
  { ic: '🛡️', s: 'Theft & security' },
  { ic: '💵', s: 'Pricing uncertainty' },
  { ic: '🚩', s: 'Bad renters' },
  { ic: '📈', s: 'Fleet growth' },
];

const TOOLBOX = [
  'Rental Agreement generator (all 50 states + DC)',
  'Pre-rental & return inspection checklists',
  '8 printable "How to Load" trailer guides',
  'Pricing & rental income calculators',
  '"Photos That Get Bookings" guide',
  'Damage & collections one-pager',
  '"Get Your First Rental" playbook',
];

const STATS = [
  { ic: '👁️', num: '1,248', lbl: 'Listing Views' },
  { ic: '👤', num: '284', lbl: 'Profile Views' },
  { ic: '💬', num: '18', lbl: 'Messages' },
  { ic: '📞', num: '31', lbl: 'Phone Clicks' },
  { ic: '🔗', num: '22', lbl: 'Website Clicks' },
];

const FAQ = [
  {
    q: 'I already have a website.',
    a: 'Good. Keep it. BigLoads gives renters another way to discover your business and sends interested people toward the business you already built.',
  },
  {
    q: 'I already use Facebook Marketplace.',
    a: 'You should. BigLoads is not asking you to stop. It is another place for renters to find your inventory, and one that does not bury your listing a day later.',
  },
  {
    q: 'I already have rental software.',
    a: 'Perfect. Keep using it. BigLoads does not replace your contracts, calendar, payments, or rental management. It focuses on discovery and leads.',
  },
  {
    q: 'Do you take a percentage?',
    a: 'No. BigLoads does not process your rental transaction and does not take a commission from it. Your rental revenue is 100% yours.',
  },
  {
    q: 'Is the $30 per trailer?',
    a: 'No. Haul Star is $30 per account, not $30 for every trailer you list. One membership covers your whole fleet.',
  },
  {
    q: 'Can I just list free?',
    a: 'Absolutely. List your trailers for free. Upgrade to Haul Star when you want the additional visibility and direct-contact features.',
  },
];

// Copy strings reused across layouts (kept here so wording never drifts).
const COPY = {
  eyebrowHero: 'For Trailer Rental Owners',
  h1a: 'Put Your Trailers in Front of ',
  h1red: 'More Renters.',
  lede: 'Keep your rates. Keep your customers. Run your rentals your way. BigLoads helps people searching for trailers in your area discover your fleet. List free, then upgrade to Haul Star when you want renters to contact your business directly.',
  ctaListFree: 'List My Trailers Free',
  ctaHowItWorks: 'See How It Works',
  microtrust: 'No commissions. No booking fees. No new rental software to learn.',
  heroBadge: 'Found by renters near you',

  channelsH: 'Add another channel.',
  channelsSub: 'You already put in the work to get found. BigLoads does not ask you to stop any of it. It just adds one more permanent place where renters actively searching for trailers can discover your fleet.',
  channelsBl: 'A permanent, trailer-specific place renters can search. More visibility. More rentals.',
  channelsNote: 'BigLoads complements the marketing you already do. It is another distribution channel, not a replacement for your website, your Facebook page, or your rental process.',

  controlH1: 'Your Trailers. Your Rates. Your Customers. ',
  controlH2red: 'Your Rules.',
  controlNote: 'BigLoads is not trying to take over your rental business. We are trying to help more people find it.',

  howEyebrow: 'How It Works',
  howH: 'BigLoads in six seconds.',
  noCut: 'BigLoads does not take a percentage of your rentals.',

  pricingEyebrow: 'Free vs Haul Star',
  pricingH: "Two simple options. You're in control.",
  pricingTableNote: '$30/month. Not $30 per trailer.',
  ctaHaulStarStart: 'Get Started With Haul Star',

  valueEyebrow: 'Do The Math',
  valueH: 'What is one extra rental worth to you?',
  valueSub: 'If Haul Star helps you land just one or two additional rentals a month, it pays for itself, and then some. You do not need hundreds of extra bookings. You need your idle trailers found by the renters already searching for them.',
  proofBigA: 'Haul Star listings get about ',
  proofBigRed: '1.8x the views',
  proofBigB: ' and roughly 20x the renter contact per view.',
  proofSmall: 'Based on our last 30 days. A Haul Star listing gets nearly double the eyeballs of a free one, and renters reach out about 20x more often per view because they can call, text, or click through directly. Small sample, real current rates, not a guarantee of bookings.',
  valuePunch: 'Your trailer cannot make money sitting in the yard.',
  valueBoxList: ['More visibility', 'More inquiries', 'More opportunities', 'Covers your whole account'],

  fbEyebrow: 'Persistent Discovery',
  fbH: 'Facebook posts disappear. Your BigLoads listing keeps working.',
  fbSub: 'Facebook Marketplace and local groups can be great sources of business. But posts get buried, timing matters, and algorithms change. BigLoads gives your trailers a permanent place on a platform built specifically around finding trailers for rent.',
  fbPunch: 'When someone searches for a trailer in your area, your listing shows up. Today, tomorrow, and next month.',
  listingCardH: '7x14 Dump Trailer',
  listingCardMeta: 'Smith Trailer Rentals · Grand Rapids, MI · 14,000 lb GVWR · Daily / Weekly Rates',

  searchEyebrow: 'Real Search Intent',
  searchH: 'Get found the moment a renter needs you.',
  searchQuery: 'dump trailer rental near me',

  painsEyebrow: 'We Get It',
  painsH: 'Built for people who actually rent trailers.',
  painsNote: 'BigLoads is being built around the real problems independent trailer rental owners deal with every day.',

  resEyebrow: 'Owner Resources',
  resH: 'Tools that make running a trailer rental business easier.',
  resGuidesH: 'Free printable loading guides.',
  resGuidesIntro: 'Give renters better instructions before they damage your equipment. Print them, laminate them, text them, or keep them with the trailer.',
  resToolboxH: 'The free Owner Toolbox.',
  newsletterH: 'The Weekly Load newsletter',
  newsletterP: 'One tip, one piece of gear, one safety lesson, one crazy trailer story. Owner shop talk, not corporate fluff.',
  newsletterCta: 'Get The Weekly Load',

  dashEyebrow: 'Owner Dashboard',
  dashH: 'See what your listings are doing.',
  dashSub: 'Track views, messages, and contact clicks so you always know whether BigLoads is producing exposure and leads. No guessing.',
  dashCta: 'Start Your Free Listing →',

  faqEyebrow: 'Yeah, but...',
  faqH: "You've got questions. We've got straight answers.",

  closeEyebrow: 'The Haul Star Upgrade',
  closeH: 'Make it easier for renters to become customers.',
  closeLede: 'Stop making interested renters hunt for you. With Haul Star, every person viewing your listing can see who you are and contact your business directly. Call you. Email you. Visit your social pages.',
  closeHeadline: 'Your Trailers. Your Customers. Your Business.',
  ctaBecomeHaulStar: 'Become a Haul Star',
  ctaNotReady: 'Not ready? List free',

  finalH: "You've already invested thousands into your trailers. Give more renters a chance to find them.",
  finalTagline: 'Real trailers. Real people. Real opportunities.',
};

// ------------------------------------------------------------------
// Layout-specific STRUCTURAL content. Some mockups (Grok especially)
// carry richer scaffolding than the shared COPY set: longer control-card
// descriptions, a break-even math table, an FB-vs-BigLoads compare, a
// 4-step search journey, pain-chip descriptions, etc. These live here so
// they stay centralized (not hardcoded in a layout file). The CORE
// marketing copy still comes from COPY/CONTROL/etc. above so the A/B core
// message stays identical; these are design-driven extras for the variant
// that renders them.
// ------------------------------------------------------------------

const CONTROL_LONG = [
  {
    ic: '\uD83D\uDE9A',
    h: 'Your Trailers',
    p: 'List the equipment you already own. Add photos, details, and your location. You control what goes on the platform.',
  },
  {
    ic: '\uD83D\uDCB0',
    h: 'Your Rates',
    p: 'Set your own daily, weekly, and weekend rates. BigLoads never touches your pricing or tells you what to charge.',
  },
  {
    ic: '\uD83D\uDC65',
    h: 'Your Customers',
    p: 'Renters contact your business directly. You deal with them the same way you always have. No middleman in the transaction.',
  },
  {
    ic: '\u2699\uFE0F',
    h: 'Your Process',
    p: 'Keep your contracts, deposits, payment methods, and rental procedures exactly as they are. BigLoads fits around your operation.',
  },
];

const STEPS_LONG = [
  {
    n: '1',
    h: 'List your trailers',
    p: 'Create a free business profile and add your inventory. Upload photos, set rates, and add your location. Takes minutes, not hours.',
  },
  {
    n: '2',
    h: 'Renters search locally',
    p: 'People looking for car haulers, dump trailers, utility trailers, and more find your listings on BigLoads. Your trailers show up when they need one.',
  },
  {
    n: '3',
    h: 'You handle the rental',
    p: 'You decide availability, pricing, deposits, payment, and rental terms. The rental is yours from start to finish. BigLoads just helps them find you.',
  },
];

const MATH_ROWS = [
  { label: 'Typical daily trailer rental', val: '$75' },
  { label: 'Typical weekend rental', val: '$140' },
  { label: 'Haul Star monthly cost', val: '$30' },
  { label: 'Rentals needed to break even', val: '1', green: true },
];

const VALUE_HAUL_ITEMS = [
  'Business name visible to renters',
  'Phone number on every listing',
  'Email address displayed',
  'Social profiles linked',
  'Direct renter contact',
  'Discount badges on your listings',
  'Insurance trust badges',
  'Zero commission on your rentals',
];

const FB_COMPARE = {
  fb: [
    { ic: '\u23F1\uFE0F', t: 'Buried in 24-48 hours' },
    { ic: '\uD83C\uDFB2', t: 'Algorithm-dependent' },
    { ic: '\u270B', t: 'Must re-post to stay visible' },
    { ic: '\uD83D\uDCF7', t: "Can't search by trailer type" },
  ],
  bl: [
    { ic: '\u26A1', t: 'Always findable in search' },
    { ic: '\uD83D\uDCCC', t: 'Permanent until you remove it' },
    { ic: '\uD83C\uDFD7\uFE0F', t: 'No re-posting required' },
    { ic: '\uD83D\uDD0D', t: 'Trailer-specific search filters' },
  ],
};

const JOURNEY = [
  {
    ic: '\uD83D\uDD0E',
    label: 'Step 1',
    query: '"dump trailer rental near me"',
    t: 'A renter in your area opens Google and searches for exactly what you have sitting in your yard.',
  },
  {
    ic: '\uD83C\uDF10',
    label: 'Step 2',
    t: 'BigLoads appears in the results. Your local trailer listing is right there. They click through to find you.',
  },
  {
    ic: '\uD83D\uDE9A',
    label: 'Step 3',
    t: 'They see your trailer, your rates, your photos. With Haul Star, your phone number and business name are right there.',
  },
  {
    ic: '\uD83D\uDCDE',
    label: 'Step 4',
    t: 'They call or message you directly. You handle the rental your way. That idle trailer just made you money.',
  },
];

const PAIN_CHIPS = [
  {
    ic: '\uD83D\uDD27',
    h: 'Damage Happens',
    p: 'Overloads, curbed tires, bent ramps, dragged jacks. Handing expensive equipment to strangers is a real risk every time.',
  },
  {
    ic: '\uD83D\uDEE3\uFE0F',
    h: 'Tire Blowouts',
    p: 'Shredded tires, bad bearings, and roadside failures mean a repair bill, disrupted rental, and lost revenue from your next booking.',
  },
  {
    ic: '\uD83D\uDD12',
    h: 'Theft and Security',
    p: "Trackers, locks, and recovery are real concerns. A missing trailer isn't just a repair cost. It's total asset loss.",
  },
  {
    ic: '\uD83D\uDCC8',
    h: 'Pricing Uncertainty',
    p: "Are you charging enough? Too much? Owners constantly wonder if they're leaving money on the table with their daily and weekend rates.",
  },
  {
    ic: '\uD83E\uDD26',
    h: 'Bad Renters',
    p: "No-shows, unprepared customers, and people who've never towed before waste your time and put your equipment at risk.",
  },
  {
    ic: '\uD83D\uDE80',
    h: 'Fleet Growth',
    p: "Trying to figure out what trailer to buy next, which types actually rent, and whether you're utilizing what you already own.",
  },
];

const RESOURCE_LIST = [
  'Rental Agreement generator (fillable, all 50 states + DC)',
  'Pre-Rental & Return Inspection Checklists (fillable)',
  '8 printable How-to-Load guides (car hauler, dump, utility, enclosed, equipment, gooseneck, motorcycle/ATV, boat)',
  'Pricing & Income calculators (towing capacity, payload, rental income)',
  'Photos That Get Bookings guide',
  'Damage & Collections one-pager',
  'Get Your First Rental playbook',
];

const HS_FEATURES = [
  'Haul Star Verified badge',
  'Business name, logo & branding displayed',
  'Phone, email & website on every listing',
  'Social links + Google Reviews imported',
  'Contact info right on your photos',
  'Priority search placement',
  'Direct renter contact, no account needed',
  'Discount badges on your listings',
  'Insurance trust badges',
  'Covers your entire account',
  'Zero commission, ever',
];

// Grok-specific copy strings (richer headlines/subs faithful to the mockup).
const GROK = {
  heroEyebrow: 'For Independent Trailer Rental Owners',
  heroBadge: "BigLoads doesn't take a cut of your rentals.",
  channelHeadA: "Don't replace your marketing.",
  channelHeadB: 'Add another distribution channel.',
  channelBlTag: 'One more permanent source',
  channelBlCopy: 'More visibility. More renters. Another way to grow.',
  channelP1: 'BigLoads complements your existing marketing by putting your trailers in front of people who are actively searching for trailers in your area.',
  channelP2: "It's another channel to help you get more renters. Not a replacement for what you already do.",
  controlLabel: "You're in charge",
  controlFootA: 'BigLoads is not trying to take over your rental business. ',
  controlFootB: 'We are trying to help people find it.',
  hiwLabel: 'Simple by design',
  hiwTitleA: 'Understand BigLoads',
  hiwTitleB: 'in 6 Seconds.',
  hiwProofA: "BigLoads doesn't take ",
  hiwProofRed: 'a percentage',
  hiwProofB: ' of your rentals.',
  hiwProofSub: 'No commission. No booking fee. No percentage. You collect your payment the same way you always have. Cash, Venmo, Square, whatever works for your business.',
  pricingLabel: 'Two options. Zero complexity.',
  pricingTitle: 'Free Listing vs. Haul Star.',
  pricingSub: 'Start free. Upgrade when you want renters to contact your business directly. Both options keep you in full control. No contracts, no commitments.',
  pricingFine: 'No credit card required for free listing. Haul Star is month-to-month, cancel any time.',
  tableNoteA: '$30/month. Not $30 per trailer.',
  tableNoteB: ' One flat price covers your entire account and every trailer you list.',
  valueLabel: 'The math is simple',
  valueTitle: 'What Is One Extra Rental Worth to You?',
  valueLedeA: "You don't need Haul Star to generate hundreds of extra rentals to make it worth it. You need it to generate ",
  valueLedeStrong: 'one or two.',
  valueSmall: 'If Haul Star helps you get just one or two additional rentals per month, it can easily pay for itself. Every rental after that is pure upside.',
  valueHaulTag: 'Upgrade to Haul Star',
  valueHaulNote: 'One account. Every trailer. No commission.',
  valueHaulFine: 'Month-to-month. Cancel any time.',
  fbLabel: 'Why a dedicated listing matters',
  fbTitle: 'Facebook Posts Disappear. Your BigLoads Listing Keeps Working.',
  fbP1: 'Facebook Marketplace and local groups can be great sources of business. You should keep using them. But posts get buried. People are not always looking when you post. Algorithms change and timing matters.',
  fbP2: 'BigLoads gives your trailers a permanent home on a platform built specifically around finding trailers for rent. Your listing shows up today, tomorrow, and next month.',
  fbColFb: 'Facebook Post',
  fbColBl: 'BigLoads Listing',
  listingBadge: 'Available Now',
  listingBiz: 'Smith Trailer Rentals',
  listingNote: 'Powered by BigLoads Haul Star',
  fbRightCap: 'When people search for trailers in your area, your listing shows up. Today, tomorrow, and next month.',
  journeyLabel: 'See it happen in real time',
  journeyTitleA: 'Your Trailer Gets Found the Moment',
  journeyTitleB: 'a Renter Needs It.',
  journeyProof: '"Your trailer cannot make money sitting in the yard." Put it in front of people actively searching for it right now.',
  journeyCtaHaulStar: 'See Haul Star Features',
  painLabel: 'We get it',
  painTitleA: 'Built for People Who',
  painTitleB: 'Actually Rent Trailers.',
  painFootA: 'BigLoads is being built around the real problems independent trailer rental owners deal with ',
  painFootRed: 'every single day.',
  painFootCta: 'See Owner Resources',
  resLabel: 'Free tools for your business',
  resTitle: 'Owner Resources That Make Running Your Business Easier.',
  resLede: "BigLoads isn't just a place to list your trailers. It's being built as the go-to resource brand for independent trailer rental operators. Practical tools you can actually use.",
  resLinkCta: 'View All Owner Resources \u2192',
  resGuidesLabel: 'Loading Guides',
  guideLabels: [
    { long: 'Car Hauler Loading Guide', short: 'Car Hauler' },
    { long: 'Dump Trailer Loading Guide', short: 'Dump Trailer' },
    { long: 'Equipment Trailer Loading Guide', short: 'Equipment Trailer' },
  ],
  dashLabel: 'Know exactly what is working',
  dashPanelTitle: 'Owner Dashboard',
  dashRightP1: 'Every Haul Star account includes an owner dashboard. Track how many people view your listings, how many click to call, and which trailers get the most attention.',
  dashRightP2: 'No guessing whether BigLoads is working. You see the exposure and the leads in real numbers.',
  faqLabel: 'Straight answers',
  faqTitle: "You've Got Questions. We've Got Answers.",
  faqCtaTitle: 'Still not sure?',
  faqCtaDesc: 'List your trailers free. See how it works. Upgrade to Haul Star only when you want direct renter contact. No risk.',
  hscEyebrow: 'The Haul Star Upgrade',
  hscSlogan: 'Your Trailers. Your Customers. Your Business.',
  hscPlanName: 'Haul Star',
  hscPlanNote: 'Covers your entire account.',
  finalTitleA: "You've Already Invested Thousands in Your Trailers. ",
  finalTitleRed: 'Give More Renters a Chance to Find Them.',
  finalSub: 'List free in minutes. Upgrade to Haul Star when you want renters calling your business directly. Real trailers. Real people. Real opportunities.',
  finalTagline: 'Real Trailers. Real People. Real Opportunities.',
};

// ------------------------------------------------------------------
// FABLE-specific STRUCTURAL content. Faithful port of
// presell-mockups/presell-FABLE.html. Icons render as inline SVG in the
// layout (Jordan: NO emojis on Fable), so these arrays carry text/keys
// only; the SVG glyph is chosen by `ic` key inside FableLayout. Core
// marketing COPY still comes from the shared COPY/TABLE_ROWS/etc. above so
// the A/B message stays identical across variants.
// ------------------------------------------------------------------

const FABLE_CHANNELS = [
  { ic: 'facebook', h: 'Facebook', p: 'Posts, groups, and Marketplace keep working for you.' },
  { ic: 'google', h: 'Google', p: 'Your Business Profile and search presence stay yours.' },
  { ic: 'word', h: 'Word of Mouth', p: 'Happy renters keep spreading the word.' },
  { ic: 'website', h: 'Your Website', p: 'Keep doing what already works.' },
];

const FABLE_CONTROL = [
  {
    num: '01',
    h: 'Your Trailers',
    p: 'List the equipment you already own, when you want, how you want. Add and remove trailers anytime.',
  },
  {
    num: '02',
    h: 'Your Rates',
    p: 'You decide what you charge. Daily, weekend, weekly, delivery fees, deposits. All your call.',
  },
  {
    num: '03',
    h: 'Your Customers',
    p: 'Renters deal directly with you. Your name, your relationship, your repeat business.',
  },
  {
    num: '04',
    h: 'Your Process',
    p: 'Keep your own contracts, payments, insurance requirements, and rental procedures. Nothing moves.',
  },
];

const FABLE_STEPS = [
  {
    n: '1',
    h: 'List your trailers',
    p: "Create your free listing in minutes. Add photos, details, rates, and your location. That's it.",
  },
  {
    n: '2',
    h: 'Renters search locally',
    p: 'People looking for car haulers, dump trailers, utility trailers, and more find your listings on BigLoads.',
  },
  {
    n: '3',
    h: 'You handle the rental',
    p: 'Availability, pricing, deposits, payments, and terms stay exactly the way you run them today.',
  },
];

const FABLE_JOURNEY = [
  {
    tag: 'Step 1 \u00B7 They Search',
    kind: 'search',
    query: 'dump trailer rental near me',
    p: 'A renter with a real job to do searches for a trailer in your area.',
  },
  {
    tag: 'Step 2 \u00B7 They Find You',
    kind: 'result',
    url: 'bigloads.com \u203A trailers \u203A dump',
    title: 'Dump Trailer Rentals Near You | BigLoads',
    desc: 'Local dump trailers available now. See rates, photos, and specs.',
    p: 'Your listing appears among local trailers built for exactly what they need.',
  },
  {
    tag: 'Step 3 \u00B7 They Contact You',
    kind: 'call',
    biz: 'Smith Trailer Rentals \u00B7 Grand Rapids, MI',
    p: 'With Haul Star, that renter calls your business directly. Your lead, your customer.',
  },
];

const FABLE_PAINS = [
  { ic: 'damage', h: 'Damage happens', p: "Overloads, curbed tires, bent fenders. Renters don't treat it like you do." },
  { ic: 'tire', h: 'Tire blowouts', p: 'One roadside failure kills the rental and the next one behind it.' },
  { ic: 'theft', h: 'Theft & security', p: 'Trackers, locks, and the fear that a trailer just disappears.' },
  { ic: 'pricing', h: 'Pricing uncertainty', p: '"Am I leaving money on the table?" is a question every owner asks.' },
  { ic: 'renters', h: 'Bad renters', p: 'No-shows, tire kickers, and people who were never tow-ready.' },
  { ic: 'growth', h: 'Fleet growth', p: 'Figuring out what to buy next, and what will actually rent.' },
];

const FABLE_GUIDES = [
  {
    img: 'carHauler',
    tag: 'Printable Guide',
    h: 'Car Hauler Loading Guide',
    p: 'Load placement, tie-down points, and a stop-before-you-tow checklist for car haulers.',
  },
  {
    img: 'dump',
    tag: 'Printable Guide',
    h: 'Dump Trailer Loading Guide',
    p: 'Material weight limits, balance, and hydraulics care that keep your dump trailer rentable.',
  },
  {
    img: 'carHauler',
    tag: 'Printable Guide',
    h: 'Equipment Trailer Loading Guide',
    p: 'Ramp use, chaining down machinery, and tongue-weight basics for equipment haulers.',
  },
];

const FABLE_RESOURCE_CHIPS = [
  'Rental Agreement generator (all 50 states + DC)',
  'Pre-Rental & Return Inspection Checklists',
  '8 printable How-to-Load guides',
  'Pricing & Income calculators',
  'Photos That Get Bookings guide',
  'Damage & Collections one-pager',
  'Get Your First Rental playbook',
];

const FABLE_STATS = [
  { num: '1,248', lbl: 'Listing Views' },
  { num: '284', lbl: 'Profile Views' },
  { num: '18', lbl: 'Messages' },
  { num: '31', lbl: 'Phone Clicks' },
  { num: '22', lbl: 'Website Clicks' },
];

const FABLE = {
  heroBadge: 'More renters. More hauls. A stronger business.',
  heroBadgeEm: 'A stronger business.',
  microtrust: ['No commissions', 'No booking fees', 'No new rental software to learn'],

  channelsEyebrow: 'Nothing Changes But Your Reach',
  channelsHa: "Don't replace your marketing.",
  channelsHb: 'Add another distribution channel.',
  channelsBlStrong: 'One more permanent path to your fleet',
  channelsBlP: 'More visibility. More renters. Another way to grow.',
  channelsNote: 'BigLoads complements the marketing you already do by putting your trailers in front of people who are actively searching for trailers in your area. It is another channel to help you get more rentals, not a replacement for anything you already built.',

  controlEyebrow: 'You Stay in Charge',
  controlHa: 'Your Trailers. Your Rates.',
  controlHb: 'Your Customers. Your Rules.',
  controlNote: 'BigLoads is not trying to take over your rental business. We are trying to help people find it.',

  howEyebrow: 'Six Seconds to Understand',
  howH: 'How It Works',
  howProofA: "BigLoads doesn't take ",
  howProofEm: 'a percentage of your rentals.',
  howProofB: ' Not now. Not ever.',
  proofStatA: 'Haul Star listings get about ',
  proofStatRed: '1.8x the views',
  proofStatB: ' and roughly 20x the renter contact per view.',
  proofStatSmall: '(Last 30 days.)',
  proofStatNote: 'Renters reach out about 20x more often per view on a Haul Star listing because they can call, text, or click through directly. Small sample, real current rates, not a guarantee of bookings.',

  pricingEyebrow: 'Two Simple Options',
  pricingH: 'Free Listing vs. Haul Star',
  pricingLede: 'Free gets you listed. Haul Star turns your listing into a storefront where renters can reach your business directly.',
  tableNote: '$30/month. Not $30 per trailer.',
  tableNoteRest: ' One membership covers your entire account, whether you run two trailers or twenty.',
  valueH: 'What is one extra rental worth to you?',
  valueP: 'You know your rates better than anyone. If Haul Star helps you get just one or two additional rentals per month, it can easily pay for itself.',
  valueBoxList: ['More visibility.', 'More inquiries.', 'More opportunities.'],
  valueTail: 'You do not need hundreds of additional rentals for Haul Star to make sense. A small marketing expense for a bigger, busier rental business. How many rentals does $30 need to generate before you are happy you spent it?',

  fbEyebrow: 'Permanent, Not Buried',
  fbH: 'Facebook posts disappear. Your BigLoads listing keeps working.',
  fbLede: 'Facebook Marketplace and local groups can be great sources of business. Keep using them. But posts get buried, people are not always looking when you post, and algorithms change without warning.',
  fbP2: 'BigLoads gives your trailers a permanent home on a platform built specifically around finding trailers for rent. When people search for trailers in your area, your listing shows up. Today, tomorrow, and next month.',
  fbCardH: '7x14 Dump Trailer',
  fbCardBiz: 'Smith Trailer Rentals',
  fbCardLoc: 'Grand Rapids, MI',
  fbCardMeta: ['14,000 lb GVWR', 'Daily / Weekly Rates'],
  fbCap: 'A Haul Star listing puts your business name and phone number right where the renter is looking.',

  searchEyebrow: 'Picture the Moment',
  searchH: 'Be there the second a renter needs a trailer.',
  searchLede: 'Somewhere near you, right now, someone needs to haul something. Here is how they find you on BigLoads.',

  painsEyebrow: 'We Get It',
  painsH: 'Built for people who actually rent trailers.',
  painsLede: 'You are not just posting an ad. You are handing expensive equipment to strangers and running a real business. We pay attention to what that actually involves.',
  painsTail: 'BigLoads is being built around the real problems independent trailer rental owners deal with every day.',

  resEyebrow: 'Free Owner Resources',
  resH: 'Tools that make running a trailer rental business easier.',
  resLede: 'Give renters better instructions before they damage your equipment. Print them, laminate them, text them to renters, or keep them with the trailer.',
  newsletterEyebrow: 'Newsletter',
  newsletterH: 'The Weekly Load',
  newsletterP: 'One business tip, one piece of gear, one maintenance tip, one crazy trailer story, one BigLoads action. Owner-first shop talk, not corporate.',
  newsletterCta: 'Subscribe Free',

  dashEyebrow: 'No Guessing',
  dashH: 'See what your listings are doing.',
  dashLede: 'Your owner dashboard shows whether your listings are getting attention. Views, messages, calls, and clicks, all in one place.',
  dashCta: 'View Your Dashboard \u2192',

  faqEyebrow: 'Yeah, But...',
  faqH: 'The questions every owner asks.',

  hsEyebrow: 'Haul Star Membership',
  hsH: 'Make it easier for renters to become customers.',
  hsLede: 'Stop making interested renters hunt for you. With Haul Star, renters see who you are and contact your business directly. Call you. Email you. Visit your social pages. Every person viewing that listing becomes a potential direct customer.',
  hsCloseLine: 'YOUR TRAILERS. YOUR CUSTOMERS. ',
  hsCloseLineEm: 'YOUR BUSINESS.',
  hsSecondaryA: 'Not ready? ',
  hsSecondaryLink: 'List your trailers free.',
  hsPrice: '$30',
  hsPer: '/month',
  hsNotPer: 'Not $30 per trailer. One membership covers your account.',
  hsOfferList: [
    'Haul Star Verified badge',
    'Business name, logo & branding on every listing',
    'Phone, email & website displayed',
    'Social media links + Google Reviews imported',
    'Contact info right on your listing photos',
    'Priority search placement + SEO-optimized listings',
    'Direct renter contact (no account needed)',
    'Discount badges renters can see and ask about',
    'Insurance trust badges on your listings',
    'Covers your entire account, not one trailer',
    'No commission on your rentals, ever',
  ],

  finalH: "You've already invested thousands into your trailers. Give more renters a chance to find them.",
  finalTag: 'Real trailers. Real people. Real opportunities.',
};

export {
  SITE,
  CANONICAL,
  TITLE,
  DESC,
  NEWSLETTER_URL,
  CHANNELS,
  CONTROL,
  STEPS,
  TABLE_ROWS,
  HS_OFFER,
  PAINS,
  TOOLBOX,
  STATS,
  FAQ,
  COPY,
  CONTROL_LONG,
  STEPS_LONG,
  MATH_ROWS,
  VALUE_HAUL_ITEMS,
  FB_COMPARE,
  JOURNEY,
  PAIN_CHIPS,
  RESOURCE_LIST,
  HS_FEATURES,
  GROK,
  FABLE_CHANNELS,
  FABLE_CONTROL,
  FABLE_STEPS,
  FABLE_JOURNEY,
  FABLE_PAINS,
  FABLE_GUIDES,
  FABLE_RESOURCE_CHIPS,
  FABLE_STATS,
  FABLE,
};
